<template>
  <ContentModal v-model="show" title="Messaging Units" size="lg">
    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-success" role="status"></div></div>
    <div v-else-if="error" class="text-center py-4 text-danger">{{ error }}</div>
    <template v-else>
      <p class="text-sm text-secondary mb-3">{{ stats?.period?.label }} · {{ stats?.location_name }}</p>
      <div class="row g-3 mb-4">
        <div class="col-md-4"><div class="summary"><i class="material-icons text-info">email</i><span>Email sent</span><strong>{{ stats?.email?.sent || 0 }}</strong></div></div>
        <div class="col-md-4"><div class="summary"><i class="material-icons text-success">sms</i><span>SMS sent</span><strong>{{ stats?.sms?.sent || 0 }}</strong></div></div>
        <div class="col-md-4"><div class="summary"><i class="material-icons text-warning">call_received</i><span>SMS received</span><strong>{{ stats?.sms?.received || 0 }}</strong></div></div>
      </div>
      <div class="chart-section">
        <div class="d-flex justify-content-between align-items-center mb-2"><h6 class="mb-0">Assignment Responses</h6><span class="text-xs text-secondary">{{ stats?.total_responses || 0 }} responses</span></div>
        <div v-if="stats?.response_methods?.length" class="chart-wrap"><canvas ref="canvas"></canvas></div>
        <p v-else class="text-center text-secondary py-4 mb-0">No assignment responses this month.</p>
      </div>
    </template>
  </ContentModal>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import Chart from 'chart.js/auto'
import ContentModal from '@/components/Modal/ContentModal.vue'
import api from '@/config/api'

const props = defineProps({ modelValue: Boolean, locationId: [Number, String] })
const emit = defineEmits(['update:modelValue'])
const show = computed({ get: () => props.modelValue, set: (value) => emit('update:modelValue', value) })
const stats = ref(null)
const loading = ref(false)
const error = ref('')
const canvas = ref(null)
let chart = null
const colors = ['#22c55e', '#0ea5e9', '#f59e0b', '#ec4899', '#6366f1', '#64748b', '#14b8a6']

function destroyChart() { if (chart) { chart.destroy(); chart = null } }
function renderChart() {
  destroyChart()
  const rows = stats.value?.response_methods || []
  if (!canvas.value || !rows.length) return
  chart = new Chart(canvas.value, {
    type: 'doughnut',
    data: { labels: rows.map((row) => row.label), datasets: [{ data: rows.map((row) => row.count), backgroundColor: colors, borderWidth: 2, borderColor: '#fff' }] },
    options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: { label: (context) => `${context.label}: ${context.raw} (${rows[context.dataIndex].percent}%)` } } } },
  })
}
async function fetchStats() {
  if (!props.locationId) return
  loading.value = true; error.value = ''
  try { stats.value = (await api.get(`/locations/${props.locationId}/messaging-stats`)).data?.data || null }
  catch (exception) { error.value = exception.response?.data?.message || 'Failed to load messaging statistics.' }
  finally { loading.value = false }
}
watch(show, async (open) => {
  if (!open) { destroyChart(); return }
  await fetchStats()
  await nextTick()
  renderChart()
})
onBeforeUnmount(destroyChart)
</script>

<style scoped>
.summary { min-height:104px; display:flex; flex-direction:column; gap:4px; padding:16px; border:1px solid #e2e8f0; border-radius:8px; background:#f8fafc; }
.summary .material-icons { font-size:22px; }
.summary span { font-size:.8rem; color:#64748b; }
.summary strong { font-size:1.5rem; color:#1e293b; }
.chart-section { border:1px solid #e2e8f0; border-radius:8px; padding:16px; }
.chart-wrap { height:320px; position:relative; }
</style>
